<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useAuthStore } from '@/store/auth-store';
import { useLoadingStore } from '@/store/loading-store';
import { loadScript } from '@/utils/scriptLoader';
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const authStore = useAuthStore();
const loadingStore = useLoadingStore();

const props = defineProps({
    buttonText: {
        type: String,
        default: '카카오로 로그인'
    }
});

const emit = defineEmits(['login-success', 'login-fail']);
const kakaoAppKey = import.meta.env.VITE_KAKAO_APP_KEY;

// 카카오 SDK 로드 및 초기화
const loadKakaoSDK = async () => {
    try {
        await loadScript('https://developers.kakao.com/sdk/js/kakao.js');
        await new Promise((resolve) => {
            const checkKakaoInterval = setInterval(() => {
                if (window.Kakao) {
                    clearInterval(checkKakaoInterval);
                    window.Kakao.init(kakaoAppKey);
                    resolve();
                }
            }, 100);
        });
        return true;
    } catch (error) {
        console.error('카카오 SDK 로드 실패:', error);
        emit('login-fail', { message: 'Kakao SDK load failed', error });
        return false;
    }
};

// 카카오 로그인 초기화 함수
const initializeKakaoLogin = () => {
    try {
        if (!window.Kakao.isInitialized()) {
            window.Kakao.init(kakaoAppKey);
        }

        return true;
    } catch (error) {
        console.error('카카오 SDK 초기화 오류:', error);
        emit('login-fail', { message: 'Kakao SDK init failed', error });
        return false;
    }
};

// 카카오 로그인 버튼 클릭 핸들러
const handleKakaoLoginClick = () => {
    if (!window.Kakao || !window.Kakao.Auth) {
        console.error('Kakao SDK가 초기화되지 않았습니다.');
        emit('login-fail', { message: 'Kakao Login not initialized' });
        return;
    }

    window.Kakao.Auth.login({
        success: (authObj) => {
            const token = authObj.access_token;
            emit('login-success', { platform: 'KAKAO', token: token });
        },
        fail: (err) => {
            console.error('카카오 로그인 실패:', err);
            emit('login-fail', { message: 'Kakao login failed', error: err });
        }
    });
};

async function getUserInfo(platform) {
    //console.log(`Bearer ${accessToken.value}`);

    try {
        loadingStore.showLoading();
        const url = `/api/customer/oauth/login`;
        const config = {
            method: 'post'
        };
        const { data, error, execute, setHeader } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                //console.log(response.data.data);
                if (!response.data.data.is_registered) {
                    const param = { site_type: response.data.data.site_type, key: response.data.data.key, name: response.data.data.name, mobile: response.data.data.mobile, email: response.data.data.email, birth_date: response.data.data.birth_date };
                    router.push({ name: 'signup-normal', query: param });
                } else {
                    handleSocialLogin(response.data.data.access_token, response.data.data.refresh_token, response.data.data.email, response.data.data.name, response.data.data.mobile, response.data.data.birth_date);
                }
            },
            onError(err) {
                console.error('Error:', err.message);
            }
        });
        setHeader('Content-Type', 'application/json');
        execute({
            access_token: `Bearer ${accessToken.value}`,
            site_type: platform
        });
    } catch (err) {
        console.error('Error:', err.message);
    } finally {
        //         nextTick(() => {
        //     window.history.replaceState(null, null, '/signup');
        //   });
        window.history.replaceState(history.state, '', '/signup');
        loadingStore.hideLoading();
    }
}

const handleSocialLogin = async (access_token, refresh_token, email, name, mobile, birth_date) => {
    try {
        loadingStore.showLoading();
        const response = await authStore.sociallogin({
            access_token: access_token,
            refresh_token: refresh_token,
            email: email,
            name: name,
            mobile: mobile,
            birth_date: birth_date
        });

        loadingStore.hideLoading();
        router.push('/');
    } catch (error) {
        loadingStore.hideLoading();
        console.error(error.response.data.msg);
        // if (toastComponentRef.value) {
        //     toastComponentRef.value.showToast('info', error.response.data.msg, '아이디 또는 패스워드를 확인하세요.');
        // }
    }
};

onMounted(async () => {
    initializeKakaoLogin();
    // const sdkLoaded = await loadKakaoSDK();
    // if (sdkLoaded) {
    //     initializeKakaoLogin();
    // }
});
</script>

<template>
    <div>
        <button @click="handleKakaoLoginClick" class="custom-kakao-button">{{ props.buttonText }}</button>
    </div>
</template>

<style scoped>
.custom-kakao-button {
    width: 100%;
    padding: 10px;
    background-color: #fee500;
    color: #000000;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-family: 'SCoreDream400', sans-serif;
    font-size: 0.93rem;
    transition: background-color 0.2s;
}
.custom-kakao-button:hover {
    background-color: #f2d900;
}
</style>
